import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { FormattedMessage, injectIntl } from 'react-intl'
import isEmpty from 'lodash/isEmpty'

import FaultCards from '../Fields/FaultCards'
import EmbeddedSelectList from '../../EmbeddedMode/Select'
import { MAX_NUMBER_OF_SUGGESTED_FAULTS } from '../../constants'
import { isEmbeddedMode } from '../../services/navigation'
import { useLinkProblemLogToIssue } from '../../hooks/Issues'
import { useGetPlantConfig } from '../../hooks/Plants'

import { Modal, Button, Flex, Input } from 'antd'
import { fetchPreviousIssuesForComponent } from '../../api/issues'
import { Row } from 'antd'

const ComponentStateModal = (props) => {
  const {
    historyForComponent,
    isOpen,
    faults,
    onClose,
    onSubmit,
    placeHolder,
    title,
    component,
    graphEdges,
    makeModelId,
    vin,
    componentType,
    identifier,
  } = props
  const linkProblemLogToIssue = useLinkProblemLogToIssue()
  const [annotation, setAnnotation] = React.useState('')
  const [reason, setReason] = React.useState(null)
  const { config: plantConfig } = useGetPlantConfig({ vin })
  const [allowLinkingIssues, setAllowLinkingIssues] = React.useState(false)
  useEffect(() => {
    setAllowLinkingIssues(!!plantConfig?.link_issues_on_rootcause_modal)
  }, [plantConfig])

  useEffect(() => {
    if (component?.category === 'splice') {
      const edge = graphEdges.filter((edge) => {
        const fromId = edge.data.from_category
          ? edge.data.from_category.substring(
              0,
              edge.data.from_category.lastIndexOf('-'),
            )
          : ''
        const toId = edge.data.to_category
          ? edge.data.to_category.substring(
              0,
              edge.data.to_category.lastIndexOf('-'),
            )
          : ''
        if (fromId === component.id || toId === component.id) {
          return edge
        }
        return false
      })

      if (edge[0]) setAnnotation(edge[0].data.harness)
    }
  }, [component, graphEdges])

  const location = useLocation()

  const [previousIssuesForComponent, setPreviousIssuesForComponent] =
    React.useState([])
  const [isLoadingIssues, setIsLoadingIssues] = React.useState(false)
  useEffect(() => {
    if (allowLinkingIssues) {
      setIsLoadingIssues(true)
      if (!!identifier && !!componentType && !!makeModelId) {
        fetchPreviousIssuesForComponent({
          component_id: identifier,
          component_type: componentType,
          make_model_id: makeModelId,
        }).then((issues) => {
          setIsLoadingIssues(false)
          setPreviousIssuesForComponent(issues)
        })
      }
    }
  }, [identifier, componentType, makeModelId, allowLinkingIssues])

  const [suggestedFaults, setSuggestedFaults] = React.useState([])
  useEffect(() => {
    setSuggestedFaults(getSuggestedFaultList())
  }, [allowLinkingIssues, previousIssuesForComponent])

  const [selectedIssueToLink, setSelectedIssueToLink] = React.useState(null)
  const [originalReason, setOriginalReason] = React.useState(null)

  const getAttachedIssueInfo = () => {
    if (
      !allowLinkingIssues ||
      isLoadingIssues ||
      isEmpty(previousIssuesForComponent)
    ) {
      return []
    }
    const res = {}
    previousIssuesForComponent.forEach((issueInfo) => {
      const issueId = issueInfo.issue.id
      const description = issueInfo.issue.description
      const reason = issueInfo.faults[0].reason
      res[issueId] = {
        id: issueId,
        description,
        reason,
      }
    })
    return Object.values(res)
  }

  const getSuggestedFaultList = () => {
    const formattedItems = []
    if (allowLinkingIssues) {
      if (isEmpty(previousIssuesForComponent)) {
        return []
      }
      const issuesAndFaults = getAttachedIssueInfo()
      issuesAndFaults.forEach((issueInfo) => {
        const labelAndVal = `${issueInfo.description} - ${issueInfo.id}`
        formattedItems.push({
          info: {
            reason: issueInfo.reason,
            issueID: issueInfo.id,
            originalReason: issueInfo.reason,
          },
          label: labelAndVal,
          value: labelAndVal,
        })
      })
    } else {
      if (isEmpty(historyForComponent)) {
        return []
      }
      const suggestedItems = Object.keys(historyForComponent).slice(
        0,
        MAX_NUMBER_OF_SUGGESTED_FAULTS,
      )
      suggestedItems.forEach((reason) => {
        const translatedDescription =
          historyForComponent[reason]?.[0]?.translated_description
        formattedItems.push({
          info: { reason: reason, issueID: null, originalReason: null },
          value: reason,
          label: translatedDescription || reason,
        })
      })
    }
    return formattedItems
  }

  const options = React.useMemo(() => ({
    allOptions: faults.map((fault) => ({
      value: fault.key,
      label: fault.translated_description || fault.key,
    })),
    suggestedOptions: suggestedFaults,
  }))

  const renderEmbedded = () => {
    const suggestedOptions = getSuggestedFaultList().map((option) => ({
      ...option,
      isSuggested: true,
    }))

    // Exclude suggested from all faults
    const otherOptions = faults
      .filter((fault) => {
        if (
          !suggestedOptions.some((suggested) => suggested.value === fault.key)
        ) {
          return fault
        }
        return false
      })
      .map((fault) => ({ value: fault.key, label: fault.key }))

    // Suggested should be on top
    const options = [...suggestedOptions, ...otherOptions]

    return (
      <Row style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}>
        <EmbeddedSelectList
          options={options}
          selected={reason}
          onChange={setReason}
        />
      </Row>
    )
  }

  const onSubmitClick = (e) => {
    e.preventDefault()
    let reasonToSubmit = reason
    if (allowLinkingIssues && !!selectedIssueToLink) {
      // we added '- <issue_id> - <issue_desc>' to the reason and
      // but saved the original in originalReason
      if (!!originalReason) {
        reasonToSubmit = originalReason
      }
    }
    onSubmit({
      annotation: annotation ? annotation : null,
      reason: reasonToSubmit,
    }).then((resp) => {
      if (allowLinkingIssues) {
        const issueID = selectedIssueToLink
        if (issueID) {
          return linkProblemLogToIssue.mutate(
            {
              issue_id: issueID,
              pl_id: resp.id,
            },
            {
              onSucces: () => onClose(),
            },
          )
        }
      }
    })

    return false
  }

  return (
    <Modal
      className="component-state-modal"
      open={isOpen}
      title={
        <div className="fault-modal-header">
          <div className="fault-modal-title">{title}</div>
          <div className="fault-modal-common-title">
            <FormattedMessage id="tracer.logFailure.title" />
          </div>
        </div>
      }
      styles={{
        body: {
          overflowY: 'scroll',
          maxHeight: 'calc(100vh - 400px)',
          minHeight: reason ? '200px' : '400px',
          scrollbarWidth: 'thin',
          scrollbarGutter: 'stable',
        },
      }}
      onOk={onSubmitClick}
      onCancel={onClose}
      footer={[
        <Button onClick={onClose}>
          <FormattedMessage id="forms.cancel" />
        </Button>,
        ...(reason
          ? [
              <Button type="primary" onClick={onSubmitClick}>
                <FormattedMessage id="forms.submit" />
              </Button>,
            ]
          : []),
      ]}
      centered={true}
    >
      {isEmbeddedMode(location.search) ? (
        renderEmbedded()
      ) : (
        <FaultCards
          options={options}
          setFault={(reason, obj) => {
            setSelectedIssueToLink(obj?.info?.issueID)
            setOriginalReason(obj?.info?.originalReason)
            setReason(reason)
          }}
          fault={reason}
          comment={annotation}
          setComment={setAnnotation}
        />
      )}
    </Modal>
  )
}

export default injectIntl(ComponentStateModal)
